import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Globe } from 'lucide-react';
const highlights = [{
  icon: <Trophy className="w-5 h-5" data-id="element-227" />,
  text: 'First Professional Win'
}, {
  icon: <Star className="w-5 h-5" data-id="element-228" />,
  text: '37 Saves in Season Opener'
}, {
  icon: <Globe className="w-5 h-5" data-id="element-229" />,
  text: 'AIHL & Asia League (ALIH)'
}];
export const SuccessStoriesSection = () => {
  return <section id="success" className="py-24 bg-navy-800 relative overflow-hidden pt-[25px] pb-[25px]" data-id="element-230">
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-3xl -mr-64 -mt-64 pointer-events-none" data-id="element-231"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-ice-500/5 rounded-full blur-3xl -ml-48 -mb-48 pointer-events-none" data-id="element-232"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-[10px]" data-id="element-233">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-234">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/10 border border-gold-500/20 rounded-full mb-6" data-id="element-235">
            <Trophy className="w-4 h-4 text-gold-500" data-id="element-236" />
            <span className="text-gold-500 text-sm font-bold tracking-wider uppercase" data-id="element-237">
              Player Spotlight
            </span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight" data-id="element-238">
            Success <span className="text-gold-500" data-id="element-239">Stories</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          delay: 0.2
        }} className="text-lg text-slate-300 max-w-2xl mx-auto" data-id="element-240">
            Real results from real players. See how UHD is helping athletes
            reach the next level.
          </motion.p>
        </div>

        {/* Featured Story Card */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.7
      }} className="bg-navy-900 border border-navy-700 rounded-sm overflow-hidden shadow-2xl" data-id="element-241">
          <div className="grid grid-cols-1 lg:grid-cols-5" data-id="element-242">
            {/* Photo Side */}
            <div className="lg:col-span-2 relative h-72 sm:h-80 lg:h-auto min-h-[400px] bg-navy-700" data-id="element-243">
              <img src="https://cdn.magicpatterns.com/uploads/4WEzZLkb68V13JKRFFDcVG/11233.png" alt="Ryo Murakami - Sydney Ice Dogs" className="w-full h-full object-cover object-top" data-id="element-244" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-navy-900" data-id="element-245"></div>

              {/* Name overlay on mobile */}
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:hidden" data-id="element-246">
                <span className="text-xs font-bold tracking-widest text-gold-500 uppercase block mb-1" data-id="element-247">
                  UHD Athlete
                </span>
                <h3 className="text-3xl font-black text-white" data-id="element-248">Ryo Murakami</h3>
                <p className="text-ice-500 font-semibold text-sm" data-id="element-249">
                  Goaltender — Sydney Ice Dogs (AIHL)
                </p>
              </div>
            </div>

            {/* Content Side */}
            <div className="lg:col-span-3 p-8 md:p-12 flex flex-col justify-center pt-[10px] pb-[10px]" data-id="element-250">
              {/* Name - desktop only */}
              <div className="hidden lg:block mb-8" data-id="element-251">
                <span className="text-xs font-bold tracking-widest text-gold-500 uppercase block mb-2" data-id="element-252">
                  UHD Athlete
                </span>
                <h3 className="text-4xl font-black text-white mb-2" data-id="element-253">
                  Ryo Murakami
                </h3>
                <p className="text-ice-500 font-semibold uppercase tracking-wider" data-id="element-254">
                  Goaltender — Sydney Ice Dogs (AIHL)
                </p>
              </div>

              {/* Highlight Pills */}
              <div className="flex flex-wrap gap-3 mb-8" data-id="element-255">
                {highlights.map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                scale: 0.9
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.3 + i * 0.1
              }} className="flex items-center gap-2 px-4 py-2 bg-navy-800 border border-navy-700 rounded-full" data-id="element-256">
                    <span className="text-ice-500" data-id="element-257">{item.icon}</span>
                    <span className="text-sm font-semibold text-white" data-id="element-258">
                      {item.text}
                    </span>
                  </motion.div>)}
              </div>

              {/* Story */}
              <div className="space-y-4 text-slate-300 leading-relaxed mb-8" data-id="element-259">
                <p data-id="element-260">
                  <span className="font-bold text-white" data-id="element-261">
                    Universal Hockey Development is proud to represent Ryo
                    Murakami!
                  </span>
                </p>
                <p data-id="element-262">
                  Our UHD Team would like to take a moment to congratulate Ryo
                  on earning his first professional win, which came in Sydney
                  with the Sydney Ice Dogs of the Australian Ice Hockey League
                  (AIHL). In the 2026 AIHL season opener Ryo made{' '}
                  <span className="text-gold-500 font-bold" data-id="element-263">37 saves</span> and
                  the Ice Dogs secured a big{' '}
                  <span className="text-gold-500 font-bold" data-id="element-264">4–2 victory</span>{' '}
                  in the league-opening matchup—better known as the{' '}
                  <span className="italic text-white" data-id="element-265">"Battle of Sydney!"</span>
                </p>
                <p data-id="element-266">
                  Since choosing Universal Hockey Development to support his
                  growth and development, Ryo has continued to elevate his game,
                  signing a contract for the 2026 AIHL season, and also earning
                  a paid tryout agreement with{' '}
                  <span className="text-white font-semibold" data-id="element-267">
                    Nikko (Japan)
                  </span>{' '}
                  in the prestigious{' '}
                  <span className="text-white font-semibold" data-id="element-268">
                    Asia League Ice Hockey (ALIH)
                  </span>
                  , for the 2026-27 season.
                </p>
              </div>

              {/* Closing Quote */}
              <div className="bg-navy-800/60 border-l-4 border-gold-500 p-6 rounded-r-sm" data-id="element-269">
                <p className="text-white font-semibold italic" data-id="element-270">
                  "Keep putting in the work, Ryo—UHD is proud of the drive,
                  discipline, and commitment you bring to the ice every day!"
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};